import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import videoDubbingJobsService, { VideoDubbingJob, CreateVideoDubbingJobDto } from '../service/video-dubbing-jobs.service';
import videoDubbingOutputsService, { VideoDubbingOutput, CreateVideoDubbingOutputDto } from '../service/video-dubbing-outputs.service';

type SpeakerMetadata = {
  speakerId: number;
  characterName: string;
  gender: 'male' | 'female';
  voiceId: string;
  description?: string;
};

type CharacterMetadata = {
  speakers: SpeakerMetadata[];
};

const CLOUDFRONT_URL = 'https://d3l5f1fjgyisw8.cloudfront.net';

const normalizeVideoUrl = (url: string): string => {
  if (!url || !url.trim()) return url;
  
  const trimmedUrl = url.trim();
  
  // Eğer zaten CloudFront URL'si varsa, olduğu gibi döndür
  if (trimmedUrl.startsWith(CLOUDFRONT_URL)) {
    return trimmedUrl;
  }
  
  // URL'den path kısmını çıkar (http://, https://, domain kısmını kaldır)
  let path = trimmedUrl;
  
  // Eğer http:// veya https:// ile başlıyorsa
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const urlObj = new URL(path);
      path = urlObj.pathname; // Sadece path kısmını al
      
      // Query string varsa ekle
      if (urlObj.search) {
        path += urlObj.search;
      }
    } catch (e) {
      // URL parse edilemezse, olduğu gibi bırak
      return trimmedUrl;
    }
  }
  
  // Path'in başında / yoksa ekle
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // CloudFront URL'si ile birleştir
  return CLOUDFRONT_URL + path;
};

const AiVideosPage: React.FC = () => {
  const [tab, setTab] = React.useState(0);
  
  // Add Video Tab States
  const [formData, setFormData] = React.useState<CreateVideoDubbingJobDto>({
    videoUrl: '',
    title: '',
    description: '',
    sourceLanguage: 'en',
    videoType: null,
    speakerCount: null,
  });
  const [speakers, setSpeakers] = React.useState<SpeakerMetadata[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [formSuccess, setFormSuccess] = React.useState(false);

  // List States
  const [jobs, setJobs] = React.useState<VideoDubbingJob[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');

  // Add Video Dialog
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingJob, setEditingJob] = React.useState<VideoDubbingJob | null>(null);
  const [editFormData, setEditFormData] = React.useState<Partial<CreateVideoDubbingJobDto>>({});

  // Transcription Details Dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState<VideoDubbingJob | null>(null);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editedSpeakers, setEditedSpeakers] = React.useState<any[]>([]);
  const [editedTranscripts, setEditedTranscripts] = React.useState<any[]>([]);
  const [saving, setSaving] = React.useState(false);

  // Dubbing Outputs
  const [dubbingOutputs, setDubbingOutputs] = React.useState<VideoDubbingOutput[]>([]);
  const [loadingOutputs, setLoadingOutputs] = React.useState(false);
  const [showAddOutputDialog, setShowAddOutputDialog] = React.useState(false);
  const [newOutputLanguage, setNewOutputLanguage] = React.useState('');
  const [selectedTranscriptionForOutput, setSelectedTranscriptionForOutput] = React.useState<string | null>(null);

  const loadJobs = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await videoDubbingJobsService.getVideoDubbingJobsPaginated(page, 30, search || undefined);
      setJobs(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  React.useEffect(() => {
    if (tab === 0) {
      loadJobs();
    }
  }, [tab, loadJobs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.videoUrl || !formData.sourceLanguage || !formData.title) {
      setFormError('Video URL, Title and Source Language are required');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setFormSuccess(false);

    try {
      // Video URL'sini normalize et (CloudFront ekle)
      const normalizedUrl = normalizeVideoUrl(formData.videoUrl);
      
      // CharacterMetadata oluştur
      const characterMetadata: CharacterMetadata | null = speakers.length > 0 ? { speakers } : null;
      
      const payload = {
        ...formData,
        videoUrl: normalizedUrl,
        characterMetadata,
      };
      
      console.log('Creating video dubbing job with payload:', payload);
      
      await videoDubbingJobsService.createVideoDubbingJob(payload);
      setFormSuccess(true);
      setFormData({
        videoUrl: '',
        title: '',
        description: '',
        sourceLanguage: 'en',
        videoType: null,
        speakerCount: null,
      });
      setSpeakers([]);
      setAddDialogOpen(false);
      loadJobs();
    } catch (e: any) {
      setFormError(e?.response?.data?.message || e?.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSpeaker = () => {
    const newSpeakerId = speakers.length > 0 ? Math.max(...speakers.map(s => s.speakerId)) + 1 : 0;
    setSpeakers([...speakers, {
      speakerId: newSpeakerId,
      characterName: '',
      gender: 'male',
      voiceId: '',
      description: '',
    }]);
  };

  const handleRemoveSpeaker = (speakerId: number) => {
    setSpeakers(speakers.filter(s => s.speakerId !== speakerId));
  };

  const handleUpdateSpeaker = (speakerId: number, field: keyof SpeakerMetadata, value: any) => {
    setSpeakers(speakers.map(s => 
      s.speakerId === speakerId ? { ...s, [field]: value } : s
    ));
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await videoDubbingJobsService.deleteVideoDubbingJob(id);
      loadJobs();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to delete job');
    }
  };

  const loadDubbingOutputs = async (dubbingJobId: string) => {
    setLoadingOutputs(true);
    try {
      const outputs = await videoDubbingOutputsService.getVideoDubbingOutputsByDubbingJobId(dubbingJobId);
      setDubbingOutputs(outputs);
    } catch (e: any) {
      console.error('Failed to load dubbing outputs:', e);
    } finally {
      setLoadingOutputs(false);
    }
  };

  const handleShowDetails = async (job: VideoDubbingJob) => {
    setSelectedJob(job);
    setIsEditMode(false);
    
    // Initialize edit states
    if (job.characterMetadata?.speakers) {
      setEditedSpeakers([...job.characterMetadata.speakers]);
    } else {
      setEditedSpeakers([]);
    }
    
    if (job.transcriptions) {
      setEditedTranscripts(job.transcriptions.map(t => ({
        ...t,
        processedTranscript: t.processedTranscript ? {
          ...t.processedTranscript,
          segments: t.processedTranscript.segments ? [...t.processedTranscript.segments] : []
        } : null
      })));
    } else {
      setEditedTranscripts([]);
    }
    
    // Load dubbing outputs
    await loadDubbingOutputs(job.id);
    
    setDetailsDialogOpen(true);
  };

  const handleCreateDubbingOutput = async () => {
    if (!selectedJob || !newOutputLanguage || !selectedTranscriptionForOutput) {
      alert('Please select a transcription and enter target language');
      return;
    }

    try {
      await videoDubbingOutputsService.createVideoDubbingOutput({
        dubbingJobId: selectedJob.id,
        transcriptionId: selectedTranscriptionForOutput,
        targetLanguage: newOutputLanguage,
        title: selectedJob.title,
        description: selectedJob.description,
      });
      
      // Reload outputs
      await loadDubbingOutputs(selectedJob.id);
      
      // Reset form
      setShowAddOutputDialog(false);
      setNewOutputLanguage('');
      setSelectedTranscriptionForOutput(null);
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to create dubbing output');
    }
  };

  const handleStartProcessing = async (outputId: string) => {
    try {
      await videoDubbingOutputsService.setCommand(outputId, 'process');
      if (selectedJob) {
        await loadDubbingOutputs(selectedJob.id);
      }
      alert('Processing command sent! The worker will pick it up soon.');
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to start processing');
    }
  };

  const handleReRender = async (outputId: string) => {
    try {
      await videoDubbingOutputsService.setCommand(outputId, 're-render');
      if (selectedJob) {
        await loadDubbingOutputs(selectedJob.id);
      }
      alert('Re-render command sent! The worker will pick it up soon.');
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to re-render');
    }
  };

  const handleDeleteOutput = async (outputId: string) => {
    if (!window.confirm('Are you sure you want to delete this dubbing output?')) return;
    
    try {
      await videoDubbingOutputsService.deleteVideoDubbingOutput(outputId);
      if (selectedJob) {
        await loadDubbingOutputs(selectedJob.id);
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to delete output');
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedJob) return;
    
    setSaving(true);
    try {
      // Update character metadata
      if (editedSpeakers.length > 0) {
        await videoDubbingJobsService.updateCharacterMetadata(selectedJob.id, {
          speakers: editedSpeakers
        });
      }
      
      // Update transcript segments for each transcription
      for (const transcript of editedTranscripts) {
        if (transcript.processedTranscript?.segments) {
          await videoDubbingJobsService.updateTranscriptSegments(
            transcript.id,
            transcript.processedTranscript.segments
          );
        }
      }
      
      // Reload jobs and close edit mode
      await loadJobs();
      setIsEditMode(false);
      alert('Changes saved successfully!');
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSpeakerInEdit = () => {
    const newSpeakerId = editedSpeakers.length > 0 ? Math.max(...editedSpeakers.map(s => s.speakerId)) + 1 : 0;
    setEditedSpeakers([...editedSpeakers, {
      speakerId: newSpeakerId,
      characterName: '',
      gender: 'male',
      voiceId: '',
      description: '',
    }]);
  };

  const handleRemoveSpeakerInEdit = (index: number) => {
    setEditedSpeakers(editedSpeakers.filter((_, i) => i !== index));
  };

  const handleOpenEdit = (job: VideoDubbingJob) => {
    setEditingJob(job);
    setEditFormData({
      title: job.title || '',
      description: job.description || '',
      videoUrl: job.videoUrl,
      sourceLanguage: job.sourceLanguage,
      videoType: job.videoType,
      speakerCount: job.speakerCount,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateJob = async () => {
    if (!editingJob) return;
    try {
      await videoDubbingJobsService.updateVideoDubbingJob(editingJob.id, editFormData);
      setEditDialogOpen(false);
      loadJobs();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to update job');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'default';
      case 'transcribing': return 'primary';
      case 'transcribed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const renderAddVideoTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Video Jobs</Typography>
        <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
          Add New Video
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Search"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="contained" onClick={handleSearch}>Search</Button>
        <Button variant="outlined" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>Clear</Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!loading && jobs.length > 0 && (
        <>
          <Paper sx={{ width: '100%', overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Video URL</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} hover>
                    <TableCell>{job.title || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {job.videoUrl}
                    </TableCell>
                    <TableCell>{job.sourceLanguage}</TableCell>
                    <TableCell>{job.videoType || '-'}</TableCell>
                    <TableCell>
                      <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
                    </TableCell>
                    <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleShowDetails(job)}
                        sx={{ mr: 1, minWidth: 'auto' }}
                      >
                        Details
                      </Button>
                      <IconButton size="small" onClick={() => handleOpenEdit(job)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(job.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {jobs.length} of {total} jobs (Page {page} of {totalPages})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}

      {!loading && jobs.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No jobs found
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>AI Videos</Typography>
      
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Add Video" />
        <Tab label="Processing" />
        <Tab label="Completed" />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && renderAddVideoTab()}
      {tab === 1 && <Typography color="text.secondary">Processing tab - Coming soon</Typography>}
      {tab === 2 && <Typography color="text.secondary">Completed tab - Coming soon</Typography>}
      {tab === 3 && <Typography color="text.secondary">Settings tab - Coming soon</Typography>}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Video Job</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            value={editFormData.title || ''}
            onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={editFormData.description || ''}
            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Video URL"
            fullWidth
            value={editFormData.videoUrl || ''}
            onChange={(e) => setEditFormData({ ...editFormData, videoUrl: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Source Language"
            fullWidth
            value={editFormData.sourceLanguage || ''}
            onChange={(e) => setEditFormData({ ...editFormData, sourceLanguage: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateJob}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Video</DialogTitle>
        <DialogContent>
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFormSuccess(false)}>
              Video job created successfully!
            </Alert>
          )}
          
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} id="add-video-form">
            <TextField
              label="Video URL"
              fullWidth
              required
              value={formData.videoUrl}
              onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
              sx={{ mt: 2, mb: 2 }}
            />
            
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Source Language"
              fullWidth
              required
              value={formData.sourceLanguage}
              onChange={(e) => setFormData({ ...formData, sourceLanguage: e.target.value })}
              placeholder="e.g., en, tr, es"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Video Type"
              fullWidth
              value={formData.videoType || ''}
              onChange={(e) => setFormData({ ...formData, videoType: e.target.value || null })}
              placeholder="e.g., story, tutorial, interview"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Speaker Count"
              fullWidth
              type="number"
              value={formData.speakerCount || ''}
              onChange={(e) => setFormData({ ...formData, speakerCount: e.target.value ? parseInt(e.target.value) : null })}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Characters / Speakers</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddSpeaker}
              >
                Add Speaker
              </Button>
            </Box>

            {speakers.map((speaker, index) => (
              <Paper key={speaker.speakerId} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Speaker {speaker.speakerId} (ID: {speaker.speakerId})</Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveSpeaker(speaker.speakerId)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>

                <TextField
                  label="Character Name"
                  fullWidth
                  size="small"
                  value={speaker.characterName}
                  onChange={(e) => handleUpdateSpeaker(speaker.speakerId, 'characterName', e.target.value)}
                  sx={{ mb: 1 }}
                />

                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={speaker.gender}
                    label="Gender"
                    onChange={(e) => handleUpdateSpeaker(speaker.speakerId, 'gender', e.target.value)}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Voice ID"
                  fullWidth
                  size="small"
                  value={speaker.voiceId}
                  onChange={(e) => handleUpdateSpeaker(speaker.speakerId, 'voiceId', e.target.value)}
                  placeholder="ElevenLabs Voice ID"
                  sx={{ mb: 1 }}
                />

                <TextField
                  label="Description (Optional)"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={speaker.description || ''}
                  onChange={(e) => handleUpdateSpeaker(speaker.speakerId, 'description', e.target.value)}
                />
              </Paper>
            ))}

            {speakers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No speakers added yet. Click "Add Speaker" to add characters.
              </Typography>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="add-video-form"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Job'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              Transcription Details
              {selectedJob && (
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedJob.title}
                </Typography>
              )}
            </Box>
            {!isEditMode && (
              <Button variant="outlined" size="small" onClick={() => setIsEditMode(true)}>
                Edit
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Job Information</Typography>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2"><strong>ID:</strong> {selectedJob.id}</Typography>
                <Typography variant="body2"><strong>Video URL:</strong> {selectedJob.videoUrl}</Typography>
                <Typography variant="body2"><strong>Language:</strong> {selectedJob.sourceLanguage}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {selectedJob.videoType || '-'}</Typography>
                <Typography variant="body2"><strong>Speaker Count:</strong> {selectedJob.speakerCount || '-'}</Typography>
                <Typography variant="body2"><strong>Status:</strong> <Chip label={selectedJob.status} color={getStatusColor(selectedJob.status)} size="small" /></Typography>
                <Typography variant="body2"><strong>Created:</strong> {new Date(selectedJob.createdAt).toLocaleString()}</Typography>
                {selectedJob.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>Description:</strong> {selectedJob.description}</Typography>
                )}
              </Paper>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Characters / Speakers
                {isEditMode && (
                  <Button size="small" onClick={handleAddSpeakerInEdit} sx={{ ml: 2 }}>
                    Add Speaker
                  </Button>
                )}
              </Typography>
              
              {!isEditMode && selectedJob.characterMetadata?.speakers && (
                <>
                  {selectedJob.characterMetadata.speakers.map((speaker: any, index: number) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Speaker {speaker.speakerId}</Typography>
                      <Typography variant="body2"><strong>Name:</strong> {speaker.characterName}</Typography>
                      <Typography variant="body2"><strong>Gender:</strong> {speaker.gender}</Typography>
                      <Typography variant="body2"><strong>Voice ID:</strong> {speaker.voiceId}</Typography>
                      {speaker.description && (
                        <Typography variant="body2"><strong>Description:</strong> {speaker.description}</Typography>
                      )}
                    </Paper>
                  ))}
                </>
              )}

              {isEditMode && editedSpeakers.map((speaker: any, index: number) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Speaker {speaker.speakerId}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveSpeakerInEdit(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    label="Character Name"
                    fullWidth
                    size="small"
                    value={speaker.characterName}
                    onChange={(e) => {
                      const updated = [...editedSpeakers];
                      updated[index].characterName = e.target.value;
                      setEditedSpeakers(updated);
                    }}
                    sx={{ mb: 1 }}
                  />
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={speaker.gender}
                      label="Gender"
                      onChange={(e) => {
                        const updated = [...editedSpeakers];
                        updated[index].gender = e.target.value;
                        setEditedSpeakers(updated);
                      }}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Voice ID"
                    fullWidth
                    size="small"
                    value={speaker.voiceId}
                    onChange={(e) => {
                      const updated = [...editedSpeakers];
                      updated[index].voiceId = e.target.value;
                      setEditedSpeakers(updated);
                    }}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={speaker.description || ''}
                    onChange={(e) => {
                      const updated = [...editedSpeakers];
                      updated[index].description = e.target.value;
                      setEditedSpeakers(updated);
                    }}
                  />
                </Paper>
              ))}

              {editedSpeakers.length === 0 && isEditMode && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No speakers added yet. Click "Add Speaker" to add characters.
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Transcriptions ({selectedJob.transcriptions?.length || 0})
              </Typography>

              {!isEditMode && selectedJob.transcriptions && selectedJob.transcriptions.length > 0 ? (
                selectedJob.transcriptions.map((transcription: any) => (
                  <Paper key={transcription.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Transcription ID: {transcription.id}</Typography>
                      <Chip 
                        label={transcription.status} 
                        color={
                          transcription.status === 'completed' ? 'success' :
                          transcription.status === 'processing' ? 'primary' :
                          transcription.status === 'failed' ? 'error' : 'default'
                        }
                        size="small" 
                      />
                    </Box>
                    
                    {transcription.audioUrl && (
                      <Typography variant="body2"><strong>Audio URL:</strong> {transcription.audioUrl}</Typography>
                    )}
                    
                    {transcription.totalDuration && (
                      <Typography variant="body2"><strong>Duration:</strong> {transcription.totalDuration.toFixed(2)}s</Typography>
                    )}
                    
                    {transcription.detectedSpeakers && (
                      <Typography variant="body2"><strong>Detected Speakers:</strong> {transcription.detectedSpeakers}</Typography>
                    )}

                    {transcription.processedTranscript && transcription.processedTranscript.segments && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}><strong>Transcript Segments:</strong></Typography>
                        <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto', bgcolor: 'white' }}>
                          {transcription.processedTranscript.segments.map((segment: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 1, pb: 1, borderBottom: idx < transcription.processedTranscript.segments.length - 1 ? '1px solid #eee' : 'none' }}>
                              <Typography variant="caption" color="text.secondary">
                                Speaker {segment.speaker} | {segment.startTime.toFixed(2)}s - {segment.endTime.toFixed(2)}s
                              </Typography>
                              <Typography variant="body2">{segment.text}</Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Box>
                    )}

                    {transcription.errorMessage && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {transcription.errorMessage}
                      </Alert>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Created: {new Date(transcription.createdAt).toLocaleString()}
                    </Typography>
                  </Paper>
                ))
              ) : !isEditMode ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No transcriptions available for this job yet.
                </Typography>
              ) : null}

              {isEditMode && editedTranscripts.map((transcription: any, tIdx: number) => (
                <Paper key={transcription.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Transcription ID: {transcription.id}</Typography>
                    <Chip label={transcription.status} size="small" />
                  </Box>

                  {transcription.processedTranscript?.segments && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}><strong>Transcript Segments (Editable):</strong></Typography>
                      <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: 'white' }}>
                        {transcription.processedTranscript.segments.map((segment: any, sIdx: number) => (
                          <Box key={sIdx} sx={{ mb: 2, pb: 2, borderBottom: sIdx < transcription.processedTranscript.segments.length - 1 ? '1px solid #eee' : 'none' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Speaker {segment.speaker} | {segment.startTime.toFixed(2)}s - {segment.endTime.toFixed(2)}s
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              value={segment.text}
                              onChange={(e) => {
                                const updated = [...editedTranscripts];
                                updated[tIdx].processedTranscript.segments[sIdx].text = e.target.value;
                                setEditedTranscripts(updated);
                              }}
                            />
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </Paper>
              ))}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Dubbing Outputs ({dubbingOutputs.length})
                </Typography>
                {!isEditMode && (
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => setShowAddOutputDialog(true)}
                    disabled={!selectedJob?.transcriptions || selectedJob.transcriptions.length === 0}
                  >
                    Create Dubbing Output
                  </Button>
                )}
              </Box>

              {loadingOutputs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : dubbingOutputs.length > 0 ? (
                dubbingOutputs.map((output) => (
                  <Paper key={output.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2">
                          {output.title || 'Untitled'} - {output.targetLanguage.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {output.id}
                        </Typography>
                      </Box>
                      <Chip 
                        label={output.status} 
                        color={
                          output.status === 'completed' ? 'success' :
                          output.status === 'failed' ? 'error' :
                          output.status === 'draft' ? 'default' : 'primary'
                        }
                        size="small" 
                      />
                    </Box>

                    {output.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Description:</strong> {output.description}
                      </Typography>
                    )}

                    {output.finalVideoUrl && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Final Video:</strong>{' '}
                        <a href={output.finalVideoUrl} target="_blank" rel="noopener noreferrer">
                          Download
                        </a>
                      </Typography>
                    )}

                    {output.command && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Command: <strong>{output.command}</strong> (Worker will process soon)
                      </Alert>
                    )}

                    {output.errorMessage && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {output.errorMessage}
                      </Alert>
                    )}

                    {output.audioSegments && output.audioSegments.length > 0 && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Audio Segments:</strong> {output.audioSegments.length} files generated
                      </Typography>
                    )}

                    {output.translatedTranscript && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Translated Segments:</strong> {output.translatedTranscript.segments.length}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      {output.status === 'draft' && !output.command && (
                        <Button 
                          size="small" 
                          variant="contained" 
                          onClick={() => handleStartProcessing(output.id)}
                        >
                          Start Processing
                        </Button>
                      )}
                      
                      {output.status === 'completed' && !output.command && (
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleReRender(output.id)}
                        >
                          Re-Render Video
                        </Button>
                      )}

                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteOutput(output.id)}
                      >
                        Delete
                      </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Created: {new Date(output.createdAt).toLocaleString()}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No dubbing outputs yet. Create one to start dubbing this video in another language.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isEditMode ? (
            <>
              <Button onClick={() => setIsEditMode(false)}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={showAddOutputDialog} onClose={() => setShowAddOutputDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Dubbing Output</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new dubbing output to translate and dub this video in another language.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Transcription</InputLabel>
            <Select
              value={selectedTranscriptionForOutput || ''}
              label="Select Transcription"
              onChange={(e) => setSelectedTranscriptionForOutput(e.target.value)}
            >
              {selectedJob?.transcriptions?.map((trans: any) => (
                <MenuItem key={trans.id} value={trans.id}>
                  {trans.title || 'Untitled'} - {trans.status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target Language</InputLabel>
            <Select
              value={newOutputLanguage}
              label="Target Language"
              onChange={(e) => setNewOutputLanguage(e.target.value)}
            >
              <MenuItem value="tr">Turkish (tr)</MenuItem>
              <MenuItem value="en">English (en)</MenuItem>
              <MenuItem value="es">Spanish (es)</MenuItem>
              <MenuItem value="fr">French (fr)</MenuItem>
              <MenuItem value="de">German (de)</MenuItem>
              <MenuItem value="it">Italian (it)</MenuItem>
              <MenuItem value="pt">Portuguese (pt)</MenuItem>
              <MenuItem value="ru">Russian (ru)</MenuItem>
              <MenuItem value="ar">Arabic (ar)</MenuItem>
              <MenuItem value="zh">Chinese (zh)</MenuItem>
              <MenuItem value="ja">Japanese (ja)</MenuItem>
              <MenuItem value="ko">Korean (ko)</MenuItem>
              <MenuItem value="custom">Custom / Other...</MenuItem>
            </Select>
          </FormControl>

          {newOutputLanguage === 'custom' && (
            <TextField
              label="Custom Language Code (ISO 639-1)"
              fullWidth
              placeholder="e.g., nl, sv, pl"
              onChange={(e) => setNewOutputLanguage(e.target.value)}
              helperText="Enter 2-letter ISO 639-1 language code"
              sx={{ mb: 2 }}
              autoFocus
            />
          )}

          <Alert severity="info">
            After creating, click "Start Processing" to begin the dubbing pipeline (Translation → TTS → Video Render).
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddOutputDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateDubbingOutput}
            disabled={!selectedTranscriptionForOutput || !newOutputLanguage}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiVideosPage;
